import profileMarkdown from "./profile.md?raw";

export type TimelineEntry = {
  title: string;
  subtitle?: string;
  time?: string;
  place?: string;
  venue?: string;
  link?: string;
  tags: string[];
  image?: {
    src: string;
    alt: string;
  };
  points: string[];
};

export type ProfileContent = {
  hero: {
    role: string;
    title: string;
    intro: string;
    location: string;
    email: string;
    github: string;
  };
  focus: string[];
  experience: TimelineEntry[];
  projects: TimelineEntry[];
  education: TimelineEntry[];
  publications: TimelineEntry[];
  patents: TimelineEntry[];
  skills: TimelineEntry[];
};

type Fields = Record<string, string>;

function splitTitle(line: string) {
  const [title, ...rest] = line.split("|").map((part) => part.trim());

  return {
    title,
    subtitle: rest.join(" | ") || undefined
  };
}

function getSection(markdown: string, name: string) {
  const match = markdown.match(
    new RegExp(`(?:^|\\n)## ${escapeRegExp(name)}\\n([\\s\\S]*?)(?=\\n## |$)`)
  );

  return match?.[1].trim() ?? "";
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getFields(markdown: string): Fields {
  return markdown
    .split("\n")
    .map((line) => line.trim())
    .reduce<Fields>((fields, line) => {
      const match = line.match(/^([A-Za-z ]+):\s+(.+)$/);

      if (match) {
        fields[match[1].toLowerCase()] = match[2].trim();
      }

      return fields;
    }, {});
}

function getList(markdown: string) {
  return markdown
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("- "))
    .map((line) => line.slice(2).trim());
}

function getImage(markdown: string, fields: Fields) {
  const src = fields.image;
  const alt = fields.imagealt ?? fields["image alt"];

  if (src) {
    return {
      src,
      alt: alt || ""
    };
  }

  const markdownImage = markdown.match(/!\[([^\]]*)\]\(([^)]+)\)/);

  if (!markdownImage) {
    return undefined;
  }

  return {
    src: markdownImage[2].trim(),
    alt: markdownImage[1].trim()
  };
}

function getTags(fields: Fields) {
  return (fields.tags ?? "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function splitEntries(markdown: string) {
  const firstEntryIndex = markdown.indexOf("### ");

  if (firstEntryIndex === -1) {
    return [];
  }

  return markdown
    .slice(firstEntryIndex)
    .split(/\n### /)
    .map((entry, index) => (index === 0 ? entry.replace(/^### /, "") : entry))
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function parseTimeline(markdown: string): TimelineEntry[] {
  return splitEntries(markdown).map((entry) => {
    const [heading = "", ...bodyLines] = entry.split("\n");
    const body = bodyLines.join("\n");
    const fields = getFields(body);
    const { title, subtitle } = splitTitle(heading);

    return {
      title,
      subtitle,
      time: fields.time,
      place: fields.place,
      venue: fields.venue,
      link: fields.link,
      tags: getTags(fields),
      image: getImage(body, fields),
      points: getList(body)
    };
  });
}

function requireField(fields: Fields, key: string) {
  const value = fields[key.toLowerCase()];

  if (!value) {
    throw new Error(`Missing required profile field: ${key}`);
  }

  return value;
}

function parseProfile(markdown: string): ProfileContent {
  const heroFields = getFields(getSection(markdown, "Hero"));

  return {
    hero: {
      role: requireField(heroFields, "Role"),
      title: requireField(heroFields, "Title"),
      intro: requireField(heroFields, "Intro"),
      location: requireField(heroFields, "Location"),
      email: requireField(heroFields, "Email"),
      github: requireField(heroFields, "GitHub")
    },
    focus: getList(getSection(markdown, "Focus")),
    experience: parseTimeline(getSection(markdown, "Experience")),
    projects: parseTimeline(getSection(markdown, "Selected Work")),
    education: parseTimeline(getSection(markdown, "Education")),
    publications: parseTimeline(getSection(markdown, "Publications")),
    patents: parseTimeline(getSection(markdown, "Patents")),
    skills: parseTimeline(getSection(markdown, "Skills"))
  };
}

export const profile = parseProfile(profileMarkdown);
